import { Stack } from '@mui/material';
import EmptyMessage from 'core/components/EmptyMessage';
import { intentionallyFloatingPromiseReturn } from 'core/utils/commonUtils';
import { isMobile } from 'core/utils/deviceDetect/selectors';
import useOrderFiles, { SWR_ORDER_FILES_ENDPOINT } from 'hooks/swr/useOrderFiles';
import { isArray, isNil } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { observer } from 'mobx-react-lite';
import { useContext, type FC } from 'react';
import * as orderFilesService from 'services/orderFileService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import OrderFileCard from './OrderFileCard';
import OrderFilesSkeleton from './OrderFilesSkeleton';
import UploadButton from './UploadButton';
import UploadDropZone from './UploadDropZone';
import type { OrderFile } from 'models/order/orderFile';

const OrderFiles: FC<OrderFilesProps> = observer(({ orderId }) => {
  const { data: files, isLoading } = useOrderFiles(orderId ?? 0, (orderId ?? 0) > 0);

  const deleteConfirm = useConfirm();

  const { notifyStore } = useContext(StoreContext);

  const Uploader = isMobile ? UploadButton : UploadDropZone;

  const deleteFile = async (item: OrderFile): Promise<void> => {
    await deleteConfirm({
      description: `Удалить "${item.name ?? ''}"?`,
    });

    const newFiles = files?.filter((m) => m.id !== item.id);

    mutate(
      (key) => isArray(key) && key[0] === SWR_ORDER_FILES_ENDPOINT,
      orderFilesService
        .deleteOrderFilesAsync(orderId ?? 0, [item.id])
        .then(() => newFiles),
      {
        optimisticData: newFiles,
        rollbackOnError: true,
        populateCache: true,
        revalidate: false,
      }
    ).catch((e) => {
      notifyStore.showNotify('Не удалось удалить изображение', 'error');
      console.error(e);
    });
  };

  if (isLoading || isNil(files)) {
    return <OrderFilesSkeleton />;
  }

  if (!files.length) {
    return (
      <Stack gap={2}>
        <Uploader />
        <EmptyMessage />
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <Uploader />

      <Stack
        gap={2}
        display={'grid'}
        gridTemplateColumns={'repeat(auto-fill, minmax(160px, 1fr))'}>
        {files.map((d, i) => (
          <OrderFileCard
            key={i}
            file={d}
            orderId={orderId ?? 0}
            onDelete={() => {
              intentionallyFloatingPromiseReturn(deleteFile)(d);
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
});

export interface OrderFilesProps {
  orderId?: number;
}

export default OrderFiles;
